import { ComponentParams, ComponentRendering } from "@sitecore-jss/sitecore-jss-nextjs";
import { Box, Container, Text } from "@chakra-ui/react";
import { Dispatch, SetStateAction, useState } from "react";
import { keyframes } from "@emotion/react";
import HeroCarouselElement, { HeroCarouselElementProps } from "./HeroCarouselElement";
import { pxToRem } from "themes/utils/mapPxToRem";

interface HeroProgressBarProps extends HeroCarouselElementProps {
    setCurrentSlideIndex: Dispatch<SetStateAction<number>>;
    setFirstLoad: Dispatch<SetStateAction<boolean>>;
    totalSlides: number;
}
interface Fields {
    Elements: HeroCarouselElementProps[];
}

export type HeroCarouselProps = {
    rendering: ComponentRendering & { params: ComponentParams };
    params: { [key: string]: string };
    fields: Fields;
};

// Seconds
const carouselDurationSeconds = 10;

const widthAnimation = keyframes`
from {
	tranform: translateX(0);
}
to {
	transform: translateX(100%);
}
`;

const HeroProgressBar = ({
    index,
    currentSlideIndex,
    setCurrentSlideIndex,
    setFirstLoad,
    totalSlides,
    ...props
}: HeroProgressBarProps) => {
    const currentSlide = currentSlideIndex === index;

    const procentageHidden = 100 / totalSlides + "%";
    return (
        <Box
            sx={{
                flexBasis: procentageHidden,
                overflow: "hidden",
            }}
        >
            <Box
                as="button"
                onClick={() => {
                    setCurrentSlideIndex(index);
                    setFirstLoad(false);
                }}
                sx={{
                    textAlign: "left",
                    textTransform: "uppercase",
                    fontWeight: "bold",
                    width: "100%",
                }}
            >
                <Text
                    as="p"
                    noOfLines={1}
                    sx={{
                        opacity: currentSlide ? "1" : ".3",
                        transition: currentSlide ? "opacity 0.3s ease-in" : "opacity .3s ease-out ",
                    }}
                    _hover={{
                        opacity: "1",
                    }}
                    _focusVisible={{
                        opacity: "1",
                    }}
                >
                    {props.fields?.SliderText.value}
                </Text>
                <Box
                    sx={{
                        bg: "rgba(255,255,255,0.3)",
                        height: "2px",
                        mt: "sp16",
                        position: "relative",
                    }}
                >
                    <Box
                        {...(currentSlide && {
                            animation: `${widthAnimation} ${carouselDurationSeconds}s linear forwards`,
                        })}
                        onAnimationEnd={() => {
                            if (currentSlideIndex < totalSlides - 1) {
                                setCurrentSlideIndex(currentSlideIndex + 1);
                            } else {
                                setCurrentSlideIndex(0);
                            }
                            setFirstLoad(false);
                        }}
                        sx={{
                            bg: currentSlide && "white",
                            height: "full",
                            width: "full",
                        }}
                    ></Box>
                </Box>
            </Box>
        </Box>
    );
};

const HeroCarousel = (props: HeroCarouselProps) => {
    const [currentSlideIndex, setCurrentSlideIndex] = useState<number>(0);
    const [firstLoad, setFirstLoad] = useState<boolean>(true);
    const totalSlides = props.fields?.Elements.length;

    return (
        <Box
            sx={{
                position: "relative",
                overflow: "hidden",
                height: { base: "100svh", lg: pxToRem("1460px") },
                color: "white",
                marginTop: [
                    "calc(var(--sizes-headerMobileTopHeight) * -1)",
                    "calc(var(--sizes-headerTopHeight) * -1)",
                ],
                "@media (max-height: 1200px)": {
                    height: { lg: pxToRem("1060px") },
                },
            }}
        >
            <>
                {props.fields?.Elements.map((element, index) => {
                    return (
                        <HeroCarouselElement
                            key={element.id + "heroitem"}
                            {...element}
                            index={index}
                            currentSlideIndex={currentSlideIndex}
                            firstLoad={firstLoad}
                        />
                    );
                })}
                <Box
                    sx={{
                        position: "absolute",
                        top: { base: "90svh", lg: "50%" },
                        left: 0,
                        zIndex: 5,
                        width: "full",
                    }}
                >
                    <Container
                        sx={{
                            display: "flex",
                            gap: "sp4",
                        }}
                    >
                        {props.fields?.Elements.map(
                            (element: HeroCarouselElementProps, index: number) => (
                                <HeroProgressBar
                                    key={element.id + "heroprogress"}
                                    {...element}
                                    setCurrentSlideIndex={setCurrentSlideIndex}
                                    setFirstLoad={setFirstLoad}
                                    totalSlides={totalSlides}
                                    currentSlideIndex={currentSlideIndex}
                                    index={index}
                                />
                            )
                        )}
                    </Container>
                </Box>
            </>
        </Box>
    );
};
export default HeroCarousel;
